import type { ConsentChoices } from "./consent-record";

/**
 * Ad attribution of this tab: UTMs and click ids of the landing URL.
 * Memory always; sessionStorage only with ad consent (§ 25 TDDDG), so reloads keep it.
 */
const STORAGE_KEY = "hv_attribution";

const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
] as const;

const GOOGLE_CLICK_ID_KEYS = ["gclid", "gbraid", "wbraid"] as const;

export type CampaignUtm = Partial<Record<(typeof UTM_KEYS)[number], string>>;

export type GoogleClickIds = Partial<
  Record<(typeof GOOGLE_CLICK_ID_KEYS)[number], string>
>;

export interface LandingAttribution {
  readonly utm: CampaignUtm | null;
  readonly google: GoogleClickIds | null;
  /** Meta click as `fb.1.<arrival ms>.<fbclid>`, the `_fbc` format fbevents writes. */
  readonly fbc: string | null;
}

export const EMPTY_ATTRIBUTION: LandingAttribution = {
  utm: null,
  google: null,
  fbc: null,
};

function pickStrings<K extends string>(
  keys: readonly K[],
  read: (key: K) => unknown,
): Partial<Record<K, string>> | null {
  const picked: Partial<Record<K, string>> = {};
  for (const key of keys) {
    const value: unknown = read(key);
    if (typeof value !== "string") continue;
    const trimmed: string = value.trim();
    if (trimmed.length > 0) picked[key] = trimmed;
  }
  return Object.keys(picked).length > 0 ? picked : null;
}

export function campaignUtmFromSearch(search: string): CampaignUtm | null {
  const params = new URLSearchParams(search);
  return pickStrings(UTM_KEYS, (key) => params.get(key));
}

/** A new ad click replaces only its own family; the same fbclid keeps its first arrival time. */
export function mergeLandingSearch(
  previous: LandingAttribution,
  search: string,
  now: number,
): LandingAttribution {
  const params = new URLSearchParams(search);
  const fbclid: string = params.get("fbclid")?.trim() ?? "";
  const sameClick: boolean = previous.fbc?.endsWith(`.${fbclid}`) === true;
  return {
    utm: campaignUtmFromSearch(search) ?? previous.utm,
    google:
      pickStrings(GOOGLE_CLICK_ID_KEYS, (key) => params.get(key)) ??
      previous.google,
    fbc:
      fbclid.length === 0 || sameClick ? previous.fbc : `fb.1.${now}.${fbclid}`,
  };
}

/** UTMs with any ad consent; click ids only with their own network's consent. */
export function consentedAttribution(
  landing: LandingAttribution,
  choices: ConsentChoices | null,
): LandingAttribution {
  if (choices === null) return EMPTY_ATTRIBUTION;
  return {
    utm: choices.google_ads || choices.meta ? landing.utm : null,
    google: choices.google_ads ? landing.google : null,
    fbc: choices.meta ? landing.fbc : null,
  };
}

function parseStoredAttribution(raw: string | null): LandingAttribution {
  if (raw === null) return EMPTY_ATTRIBUTION;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return EMPTY_ATTRIBUTION;
  }
  if (typeof parsed !== "object" || parsed === null) return EMPTY_ATTRIBUTION;
  const record: object = parsed;
  const nested =
    (name: string) =>
    (key: string): unknown => {
      const value: unknown = Reflect.get(record, name);
      return typeof value === "object" && value !== null
        ? Reflect.get(value, key)
        : undefined;
    };
  const fbc: unknown = Reflect.get(record, "fbc");
  return {
    utm: pickStrings(UTM_KEYS, nested("utm")),
    google: pickStrings(GOOGLE_CLICK_ID_KEYS, nested("google")),
    fbc: typeof fbc === "string" && fbc.length > 0 ? fbc : null,
  };
}

/** Reload: the stored tab copy fills gaps, values of the current URL win. */
export function fillFromStored(
  landing: LandingAttribution,
  raw: string | null,
): LandingAttribution {
  const stored: LandingAttribution = parseStoredAttribution(raw);
  return {
    utm: landing.utm ?? stored.utm,
    google: landing.google ?? stored.google,
    fbc: landing.fbc ?? stored.fbc,
  };
}

function cookieValue(cookies: string, name: string): string | null {
  for (const pair of cookies.split(";")) {
    const [key, ...rest] = pair.trim().split("=");
    if (key !== name) continue;
    const value: string = rest.join("=").trim();
    return value.length > 0 ? value : null;
  }
  return null;
}

/** Booking metadata. fbc/fbp prefer the pixel's own cookies; fbc falls back to the landing click. */
export function bookingClickIds(
  landing: LandingAttribution,
  choices: ConsentChoices | null,
  cookies: string,
): Record<string, string> {
  const allowed: LandingAttribution = consentedAttribution(landing, choices);
  const ids: Record<string, string> = {};
  for (const [key, value] of Object.entries(allowed.google ?? {})) {
    if (value) ids[key] = value;
  }
  if (choices?.meta === true) {
    const fbc: string | null = cookieValue(cookies, "_fbc") ?? allowed.fbc;
    const fbp: string | null = cookieValue(cookies, "_fbp");
    if (fbc !== null) ids.fbc = fbc;
    if (fbp !== null) ids.fbp = fbp;
  }
  return ids;
}

const tab = {
  landing: EMPTY_ATTRIBUTION,
  consent: null as ConsentChoices | null,
  restored: false,
};

function readStored(): string | null {
  try {
    return window.sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function persist(): void {
  const kept: LandingAttribution = consentedAttribution(
    tab.landing,
    tab.consent,
  );
  try {
    if (kept.utm === null && kept.google === null && kept.fbc === null) {
      window.sessionStorage.removeItem(STORAGE_KEY);
    } else {
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(kept));
    }
  } catch {
    // Storage blocked: memory still serves this page life.
  }
}

/** Website mount and route change: the latest ad URL of this tab. */
export function rememberLandingAttribution(): void {
  if (typeof window === "undefined") return;
  tab.landing = mergeLandingSearch(
    tab.landing,
    window.location.search,
    Date.now(),
  );
  if (tab.consent !== null) persist();
}

/** Banner decision: restores the tab copy once, then keeps only what this consent allows. */
export function applyAttributionConsent(choices: ConsentChoices | null): void {
  if (typeof window === "undefined") return;
  tab.consent = choices;
  if (
    !tab.restored &&
    (choices?.google_ads === true || choices?.meta === true)
  ) {
    tab.restored = true;
    tab.landing = fillFromStored(tab.landing, readStored());
  }
  persist();
}

/** Current URL first, else the remembered landing. */
export function readCampaignUtm(): CampaignUtm | undefined {
  if (typeof window === "undefined") return undefined;
  return (
    campaignUtmFromSearch(window.location.search) ??
    tab.landing.utm ??
    undefined
  );
}

/** Click ids for the booking, each only with its network's consent. */
export function readBookingClickIds(): Readonly<Record<string, string>> {
  if (typeof window === "undefined") return {};
  return bookingClickIds(tab.landing, tab.consent, document.cookie);
}
